import React, { useEffect, useState, useRef } from 'react';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Badge from '@mui/material/Badge';
import SvgIcon from '@mui/material/SvgIcon';
import muvikaAttrs from './../../attrs';
import { ReactComponent as EqualizerIcon } from './../../assets/equalizer.svg';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ThemeToggleButton from './../items/ThemeToggleButton';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import ListItemText from '@mui/material/ListItemText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HomeIcon from '@mui/icons-material/Home';
import AccountCircle from '@mui/icons-material/AccountCircle';
import TR from './../../Trans';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import settingsStore from '../../stores/settings';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import SearchIcon from '@mui/icons-material/Search';
import GradeIcon from '@mui/icons-material/Grade';
import AlbumIcon from '@mui/icons-material/Album';
import MicIcon from '@mui/icons-material/Mic';
import { LightMode, DarkMode } from "@mui/icons-material";
import SettingsIcon from '@mui/icons-material/Settings';
import navigationStore from './../../stores/navigation';
import { Views } from './../../enums/enums';
import { useLocalStorage } from '../../services/useLocalStorage';
import sessionStore from './../../stores/session';
import environmentStore from '../../stores/environment';
import TermsDialog from '../dialogs/TermsDialog';
import accountStore from '../../stores/account';
import version from './../../version';
import musicPlayerStore from '../../stores/musicPlayer';
import playerStore from '../../stores/player';
import songbookQueueStore from '../../stores/songbookQueue';

function SideBar() {
  const sessionToken = sessionStore((state) => state.sessionToken);
  const goToView = navigationStore((state) => state.goToView);
  const safeArea = navigationStore((state) => state.safeArea);
  const currentView = navigationStore((state) => state.currentView);
  const environment = environmentStore((state) => state.environment);
  const getEnvironment = environmentStore((state) => state.getEnvironment);
  const settings = settingsStore((state) => state.settings);
  const getSettings = settingsStore((state) => state.getSettings);
  const account = accountStore((state) => state.account);
  const getAccount = accountStore((state) => state.getAccount);
  const isMusicPaused = musicPlayerStore((state) => state.isPaused);
  const isPlayerStopped = playerStore((state) => state.isStopped);
  const setSettings = settingsStore((state) => state.setSettings);
  const isSettingsWaiting = settingsStore((state) => state.isWaiting);
  const queue = songbookQueueStore((state) => state.queue);

  const sidebarRef = useRef();

  const [lastQueueCount, setLastQueueCount] = useState(null);

  const [isBlinking, setBlinking] = useState(false);
  const [theme, setTheme] = useLocalStorage(muvikaAttrs.service + "-theme", "dark");

  const [isPrivacyDialogOpen, setIsPrivacyDialogOpen] = useState(false);
  const [isBgMusicOn, setIsBgMusicOn] = useState(false);

  const handleOpenPrivacyDialog = () => {
    setIsPrivacyDialogOpen(true);
  };

  const handleClosePrivacyDialog = () => {
    setIsPrivacyDialogOpen(false);
  };

  const [isScrollVisible, setScrollVisible] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);


  const checkScrollbar = () => {
    let paperEl = document.getElementById("muvika-sidebar-paper");
    if (paperEl) {
      const hasScrollbar =
        paperEl.scrollHeight > paperEl.clientHeight;
      console.log("hasScrollbar", hasScrollbar);
      setScrollVisible(hasScrollbar);
      let scrollbarWidth = paperEl.offsetWidth - paperEl.clientWidth;
      console.log("scrollbarWidth", scrollbarWidth);
      setScrollbarWidth(scrollbarWidth);
      window.dispatchEvent(new Event('refitbuttons'));
      window.dispatchEvent(new Event('adjustplayqueuebottom'));
    }
  };

  useEffect(() => {
    checkScrollbar();
  }, [isBgMusicOn]);

  useEffect(() => {
    if (lastQueueCount !== null) {
      if (queue?.length > lastQueueCount) {
        setBlinking(true);
        setTimeout(() => {
          setBlinking(false);
        }, 3000);
      }
    }
    setLastQueueCount(queue ? queue.length : 0);
  }, [queue]);

  useEffect(() => {
    if (!environment) {
      getEnvironment(sessionToken);
    }
    if (!settings) {
      getSettings(sessionToken);
    }
    if (!account) {
      getAccount(sessionToken);
    }
    checkScrollbar();
    window.addEventListener("resize", checkScrollbar);
    window.addEventListener("scroll", checkScrollbar);
  }, []);

  useEffect(() => {
    checkScrollbar();
  }, [environment, settings, account]);

  const renderHomeLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Home)} selected={currentView == Views.Home}>
        <ListItemIcon>
          <HomeIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_home} />
      </ListItemButton>
    </ListItem>;
  };

  const renderNewTracksLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => {
        console.log("new tracks rendered click");
        goToView(Views.NewTracks);
      }} selected={currentView == Views.NewTracks}>
        <ListItemIcon>
          <FiberNewIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_new} />
      </ListItemButton>
    </ListItem>;
  };

  const renderTracksLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Catalog)} selected={currentView == Views.Catalog}>
        <ListItemIcon>
          <MusicNoteIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_browse} />
      </ListItemButton>
    </ListItem>;
  };

  const renderSearchLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Search)} selected={currentView == Views.Search}>
        <ListItemIcon>
          <SearchIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_search} />
      </ListItemButton>
    </ListItem>;
  };

  const renderPlaylistsLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.PlayLists)} selected={currentView == Views.PlayLists}>
        <ListItemIcon>
          <PlaylistPlayIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_playlists} />
      </ListItemButton>
    </ListItem>;
  };

  const renderFavoritesLink = () => {
    if (settings) {
      if (!settings.usage_mode || settings.usage_mode === "restaurant") {
        return <ListItem disablePadding>
          <ListItemButton onClick={() => goToView(Views.Favorites)} selected={currentView == Views.Favorites}>
            <ListItemIcon>
              <GradeIcon />
            </ListItemIcon>
            <ListItemText primary={TR.cm_favorites} />
          </ListItemButton>
        </ListItem>;
      }
    }
  };

  const renderBgExpand = () => {
    if (isBgMusicOn) {
      return <ExpandLessIcon />;
    } else {
      return <ExpandMoreIcon />;
    }
  };

  const showMusicIcon = () => {
    if (account) {
      if (!(account.subscription.valid == 0)) {
        if (!isMusicPaused.isPaused && isPlayerStopped.isStopped) {
          return <div><img className="muvika-icon performance-list-item-icon" src="./eq.gif" /></div>;
        }
      }
    }
    return <AlbumIcon />;
  };

  const renderMusicLink = () => {
    return <ListItem disablePadding sx={{ wordWrap: "nowrap" }}>
      <ListItemButton onClick={() => {
        if (isBgMusicOn) {
          setIsBgMusicOn(false);
        } else {
          setIsBgMusicOn(true);
        }
        setTimeout(() => {
          window.dispatchEvent(new Event('refitbuttons'));
          window.dispatchEvent(new Event('resize'));
        }, 100);
      }} selected={currentView == Views.BackgroundMusic}>
        <ListItemIcon>
          {showMusicIcon()}
        </ListItemIcon>
        <ListItemText sx={{ minWidth: "7rem !important" }} primary={TR.cm_background_music} />
        {renderBgExpand()}
      </ListItemButton>
    </ListItem>;
  };

  const renderMusicPlayerLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.BackgroundMusicPlayer)} selected={currentView == Views.BackgroundMusicPlayer}>
        <ListItemIcon>
          <QueueMusicIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_bg_player} />
      </ListItemButton>
    </ListItem>;
  };

  const renderMusicStylesLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.BackgroundMusicStyles)} selected={currentView == Views.BackgroundMusicStyles}>
        <ListItemIcon>
          <MusicNoteIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_bg_styles} />
      </ListItemButton>
    </ListItem>;
  };

  const renderMusicSearchLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.BackgroundMusicSearch)} selected={currentView == Views.BackgroundMusicSearch}>
        <ListItemIcon>
          <SearchIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_search} />
      </ListItemButton>
    </ListItem>;
  };

  const renderMusicPlaylistsLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.BackgroundMusicPlaylists)} selected={currentView == Views.BackgroundMusicPlaylists}>
        <ListItemIcon>
          <PlaylistPlayIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_playlists} />
      </ListItemButton>
    </ListItem>;
  };

  const renderSingersLink = () => {
    if (settings) {
      if (!settings.usage_mode || settings.usage_mode === "restaurant") {
        return <ListItem disablePadding>
          <ListItemButton onClick={() => goToView(Views.Singers)} selected={currentView == Views.Singers}>
            <ListItemIcon>
              <MicIcon />
            </ListItemIcon>
            <ListItemText primary={TR.cm_singers} />
          </ListItemButton>
        </ListItem>;
      }
    }
  };

  const renderAccountLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Account)} selected={currentView == Views.Account}>
        <ListItemIcon>
          <AccountCircle />
        </ListItemIcon>
        <ListItemText primary={TR.ww_your_account_information} />
      </ListItemButton>
    </ListItem>;
  };

  const renderSettingsLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Settings)} selected={currentView == Views.Settings}>
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_settings} />
      </ListItemButton>
    </ListItem>;
  };

  const renderFeedbackLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Feedback)} selected={currentView == Views.Feedback}>
        <ListItemIcon>
          <FeedbackIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_feedback} />
      </ListItemButton>
    </ListItem>;
  };

  const renderPrivacyLink = () => {
    return <ListItem disablePadding>
      <ListItemButton onClick={handleOpenPrivacyDialog}>
        <ListItemIcon>
          <PrivacyTipIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_register_clause} />
      </ListItemButton>
    </ListItem>;
  };

  const renderTermsDialog = () => {
    if (account) {
      if (account.tags.includes("consumer")) {
        return <TermsDialog
          open={isPrivacyDialogOpen}
          onClose={handleClosePrivacyDialog}
          title={TR.cm_register_clause}
          url={environment ? environment.urls.url_privacy_home : ""}
        />;
      } else {
        return <TermsDialog
          open={isPrivacyDialogOpen}
          onClose={handleClosePrivacyDialog}
          title={TR.cm_register_clause}
          url={environment ? environment.urls.url_privacy_pro : ""}
        />;
      }
    }
  };

  const renderSongbookQueueLink = () => {
    if (account) {
      if (!account.tags.includes("consumer")) {
        return <Badge className="muvika-sidebar-badge" badgeContent={queue?.length} color="primary">
          <ListItem className={isBlinking ? "blink-three-times" : ""} disablePadding>
            <ListItemButton onClick={() => goToView(Views.SongbookQueue)} selected={currentView == Views.SongbookQueue}>
              <ListItemIcon>
                <PlaylistAddCheckIcon />
              </ListItemIcon>
              <ListItemText primary={TR.cm_songbook_queue} />
            </ListItemButton>
          </ListItem>
        </Badge>;
      }
    }
  };

  const renderMusicSublist = () => {
    if (isBgMusicOn) {
      return <List className="muvika-sidebar-sublist">
        {renderMusicPlayerLink()}
        {renderMusicStylesLink()}
        {renderMusicSearchLink()}
        {renderMusicPlaylistsLink()}
      </List>;
    }
  };

  const renderVersion = () => {
    if (settings) {
      if (settings.usage_mode === "booth") {
        return <ListItem disablePadding>
          <ListItemButton className="muvika-version-number">
            <small>{version} {TR.cm_booth_mode} {muvikaAttrs.side === "dev" ? "no_rep" : ""}</small>
          </ListItemButton>
        </ListItem>;
      }
    }
    return <ListItem disablePadding>
      <ListItemButton className="muvika-version-number">
        <small>{version} {muvikaAttrs.side === "dev" ? "no_rep" : ""}</small>
      </ListItemButton>
    </ListItem>;
  };

  const toggleTheme = () => {
    if (settings) {
      let newSettings = { ...settingsStore.getState().settings };
      if (settings.theme === "light") {
        document.documentElement.classList.remove("light-theme");
        setTheme("dark");
        newSettings.theme = "dark";
      } else {
        document.documentElement.classList.add("light-theme");
        setTheme("light");
        newSettings.theme = "light";
      }
      setSettings(sessionToken, newSettings, false, true);
    }
  };


  const renderThemeLink = () => {
    if (settings) {
      if (settings.theme === "dark") {
        return <ListItem disablePadding>
          <ListItemButton disabled={isSettingsWaiting} onClick={() => toggleTheme()}>
            <ListItemIcon>
              <LightMode />
            </ListItemIcon>
            <ListItemText primary={TR.cm_set_light_theme} />
          </ListItemButton>
        </ListItem>;
      } else {
        return <ListItem disablePadding>
          <ListItemButton disabled={isSettingsWaiting} onClick={() => toggleTheme()}>
            <ListItemIcon>
              <DarkMode />
            </ListItemIcon>
            <ListItemText primary={TR.cm_set_dark_theme} />
          </ListItemButton>
        </ListItem>;
      }
    }
  };

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      PaperProps={{
        ref: sidebarRef,
        id: "muvika-sidebar-paper",
      }}
      sx={{ width: isScrollVisible ? "calc(11rem + " + scrollbarWidth + "px)" : "11rem", background: "var(--bg-color) !important", "& .MuiPaper-root": { width: isScrollVisible ? "calc(11rem + " + scrollbarWidth + "px)" : "11rem", background: "transparent", maxHeight: "95%" } }}
    >
      {renderTermsDialog()}

      <List className="muvika-sidebar-list" sx={{
        paddingTop: safeArea ? (safeArea.top + 9) + "px !important" : "0.6rem !important",
        paddingLeft: safeArea ? safeArea.left + "px" : 0,


      }}>
        {renderHomeLink()}
        {renderNewTracksLink()}
        {renderTracksLink()}
        {renderSearchLink()}
      </List>
      <List className="muvika-sidebar-list" sx={{
        paddingLeft: safeArea ? safeArea.left + "px" : 0
      }}>
        {renderPlaylistsLink()}
        {renderFavoritesLink()}
        {renderSingersLink()}
      </List>
      <List className="muvika-sidebar-list" sx={{
        paddingLeft: safeArea ? safeArea.left + "px" : 0
      }}>
        {renderSongbookQueueLink()}
        {renderMusicLink()}
        {renderMusicSublist()}
        {/* <ListItem disablePadding>
          <ListItemButton onClick={() => goToView(Views.BackgroundMusic)} selected={currentView == Views.BackgroundMusic}>
            <ListItemIcon>
              <AlbumIcon />
            </ListItemIcon>
            <ListItemText primary={TR.cm_background_music} />
          </ListItemButton>
        </ListItem> */}
      </List>
      <List className="muvika-sidebar-list" sx={{
        paddingLeft: safeArea ? safeArea.left + "px" : 0
      }}>
        {renderAccountLink()}
        {renderSettingsLink()}
        {renderFeedbackLink()}
        {renderPrivacyLink()}
        {renderThemeLink()}
        {renderVersion()}
      </List>

    </Drawer>
  );
}

export default SideBar;
